import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authService, AuthCheckError } from '../services/authService'
import { setAuthToken } from '../services/authToken'

// How many times to retry the session-restore check when it fails for a
// reason that says nothing about the cookie itself (a deploy mid-rollover,
// a dropped connection) before giving up and showing the login screen.
const SESSION_RESTORE_RETRIES = 2
const SESSION_RESTORE_RETRY_DELAY_MS = 1500

interface User {
  id: number
  email: string
  role: string
  onboardingState?: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshOnboardingState: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    // A deploy (frontend or backend) can land the session-restore check in a
    // window where the API is mid-rollover: the request fails, but the cookie
    // it would have checked is perfectly fine. Clearing the session on that —
    // the same as a genuine 401 — forced a relogin on every redeploy for a
    // reader who had done nothing wrong, so only a real "no session" answer
    // (401/403) clears it; anything else gets a couple of retries first.
    let cancelled = false
    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

    const restoreSession = async (attempt: number): Promise<void> => {
      try {
        const userData = await authService.getCurrentUser()
        if (!cancelled) setUser(userData)
      } catch (error) {
        const recoverable = error instanceof AuthCheckError && error.isRecoverable
        if (recoverable && attempt < SESSION_RESTORE_RETRIES) {
          await sleep(SESSION_RESTORE_RETRY_DELAY_MS * (attempt + 1))
          if (!cancelled) await restoreSession(attempt + 1)
          return
        }
        // Either a real 401/403 (not logged in), or retries are exhausted —
        // at that point there is nothing left to do but show the login
        // screen, the same as before this existed.
        if (!cancelled) setUser(null)
      }
    }

    const initializeAuth = async () => {
      try {
        // Remove any token persisted by older app versions — the session now
        // lives in an HttpOnly cookie, not localStorage.
        localStorage.removeItem('token')
        localStorage.removeItem('token_expiry')

        await restoreSession(0)
      } catch (error) {
        console.error('Auth initialization error:', error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    initializeAuth()
    return () => {
      cancelled = true
    }
  }, [])

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await authService.login({ email, password })

      // Get token from response (prioritize 'token' over 'access_token').
      // Kept only in memory for this session; persistence is via the cookie.
      const token = response.token || response.access_token || ''

      const userData = {
        id: parseInt(response.user.id),
        email: response.user.email,
        role: response.userData?.role || 'user',
        onboardingState: (response.user as any).onboardingState,
      }

      setAuthToken(token)
      setToken(token)
      setUser(userData)
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  }

  const signup = async (email: string, password: string): Promise<void> => {
    try {
      const response = await authService.signup({ email, password })

      const token = response.token || response.access_token || ''

      const userData = {
        id: parseInt(response.user.id),
        email: response.user.email,
        role: response.userData?.role || 'user',
        onboardingState: (response.user as any).onboardingState,
      }

      setAuthToken(token)
      setToken(token)
      setUser(userData)
    } catch (error) {
      throw error
    }
  }

  const refreshOnboardingState = async () => {
    try {
      const u = await authService.getCurrentUser()
      setUser(prev => prev ? { ...prev, onboardingState: (u as any).onboardingState } : prev)
    } catch (error) {
      console.error('Failed to refresh onboarding state:', error)
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setAuthToken(null)
      setUser(null)
      setToken(null)
      localStorage.removeItem('token')
      localStorage.removeItem('token_expiry')
    }
  }

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    login,
    signup,
    logout,
    refreshOnboardingState,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}